import React, { useContext, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";

import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import LogoutIcon from "@mui/icons-material/Logout";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import WarningIcon from "@mui/icons-material/Warning";
import CreditCardIcon from "@mui/icons-material/CreditCard";

import { Context as AuthContext } from "../../context/Auth";
import {
    useService as useBillingService,
    SubscriptionPlan,
    PlanMetadata,
} from "../../services/Billing";

import Button from "../../components/Button";
import SectionCard from "../../components/SectionCard";
import ErrorAlert from "../../components/ErrorAlert";
import useLogout from "../../hooks/useLogout";

interface Subscription {
    id: string;
    status: string;
    currentPeriodStart: string;
    currentPeriodEnd: string;
    cancelAtPeriodEnd: boolean;
    priceId: string;
    productId: string;
}

function formatDate(dateString: string): string {
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString(undefined, {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    } catch {
        return dateString;
    }
}

function getStatusChip(status: string | null) {
    if (!status) {
        return (
            <Chip
                label="No subscription"
                color="default"
                size="small"
                icon={<CancelIcon />}
            />
        );
    }

    const statusConfig: Record<
        string,
        { label: string; color: any; icon: React.ReactElement }
    > = {
        active: {
            label: "Active",
            color: "primary",
            icon: <CheckCircleIcon />,
        },
        canceled: {
            label: "Canceled",
            color: "default",
            icon: <CancelIcon />,
        },
        past_due: {
            label: "Past due",
            color: "error",
            icon: <WarningIcon />,
        },
        unpaid: {
            label: "Unpaid",
            color: "error",
            icon: <WarningIcon />,
        },
        incomplete: {
            label: "Incomplete",
            color: "warning",
            icon: <WarningIcon />,
        },
        incomplete_expired: {
            label: "Expired",
            color: "error",
            icon: <CancelIcon />,
        },
        paused: {
            label: "Paused",
            color: "warning",
            icon: <WarningIcon />,
        },
    };

    const config = statusConfig[status] || {
        label: status,
        color: "default",
        icon: <CancelIcon />,
    };

    return (
        <Chip
            label={config.label}
            color={config.color}
            size="small"
            icon={config.icon}
        />
    );
}

export default function DashboardSettings() {
    const { state: authState } = useContext(AuthContext);
    const billingService = useBillingService();
    const logout = useLogout();

    const [loading, setLoading] = useState(true);
    const [subscription, setSubscription] = useState<Subscription | null>(null);
    const [currentPlan, setCurrentPlan] = useState<SubscriptionPlan | null>(
        null,
    );
    const [plans, setPlans] = useState<PlanMetadata[]>([]);
    const [hasUnlimitedAccess, setHasUnlimitedAccess] = useState(false);
    const [characterLimit, setCharacterLimit] = useState<number | null>(null);
    const [charactersUsed, setCharactersUsed] = useState<number>(0);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);

                const [plansData, subscriptionData] = await Promise.all([
                    billingService.getPlans(),
                    billingService.getSubscription(),
                ]);

                setSubscription(subscriptionData.subscription);
                setCurrentPlan(subscriptionData.plan);
                setHasUnlimitedAccess(
                    subscriptionData.hasUnlimitedAccess ||
                        plansData.hasUnlimitedAccess ||
                        false,
                );
                setCharacterLimit(
                    subscriptionData.characterLimit ??
                        plansData.characterLimit ??
                        200, // Default free tier
                );
                setCharactersUsed(
                    subscriptionData.charactersUsed ??
                        plansData.charactersUsed ??
                        0,
                );
                setPlans(plansData.plans);
            } catch (err: any) {
                console.error("Failed to fetch billing data", err);
                setError(err.message || "Failed to load billing information");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [billingService]);

    // Redirect to login if not authenticated
    if (!authState.user) {
        return <Navigate to="/login" replace />;
    }

    const handleManageSubscription = async () => {
        try {
            setLoading(true);
            const returnUrl = `${window.location.origin}/dashboard/settings`;
            const { url } = await billingService.createPortalSession({
                returnUrl,
            });
            window.location.href = url;
        } catch (err: any) {
            console.error("Failed to create portal session", err);
            setError(err.message || "Failed to open customer portal");
            setLoading(false);
        }
    };

    return (
        <Container
            disableGutters
            sx={{
                width: "100%",
                mx: "auto",
                maxWidth: {
                    xs: "100%",
                    sm: 720,
                    md: 760,
                },
                mt: { xs: 1, sm: 2 },
                pb: { xs: 2, sm: 3 },
            }}
        >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {error && <ErrorAlert message={error} />}

                {/* Unlimited Access Card */}
                {hasUnlimitedAccess && (
                    <SectionCard title="Subscription">
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                mt: 4,
                                mb: 2,
                            }}
                        >
                            <CheckCircleIcon color="success" />
                            <Box>
                                <Typography
                                    variant="h6"
                                    sx={{ fontWeight: 600 }}
                                >
                                    Unlimited Access
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Your account has unlimited access to all
                                    features. No subscription required.
                                </Typography>
                            </Box>
                        </Box>
                    </SectionCard>
                )}

                {/* Usage Display Card */}
                <SectionCard title="Usage">
                    {loading ? (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                py: 2,
                            }}
                        >
                            <CircularProgress size={24} />
                        </Box>
                    ) : (
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: "block", mb: 0.5 }}
                                >
                                    {hasUnlimitedAccess
                                        ? "All-Time Usage"
                                        : "Usage"}
                                </Typography>
                                <Typography
                                    variant="subtitle1"
                                    sx={{ fontWeight: 700 }}
                                >
                                    {hasUnlimitedAccess ? (
                                        charactersUsed.toLocaleString()
                                    ) : (
                                        <>
                                            {charactersUsed.toLocaleString()} /{" "}
                                            {characterLimit !== null
                                                ? characterLimit.toLocaleString()
                                                : "∞"}
                                        </>
                                    )}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: "block", mb: 0.5 }}
                                >
                                    Current Plan
                                </Typography>
                                <Typography variant="body2">
                                    {hasUnlimitedAccess
                                        ? "Unlimited"
                                        : currentPlan
                                          ? plans.find(
                                                (p) => p.id === currentPlan,
                                            )?.name || currentPlan
                                          : "Free Plan"}
                                </Typography>
                            </Grid>
                        </Grid>
                    )}
                </SectionCard>

                {/* Subscription Status Card */}
                {subscription && !hasUnlimitedAccess && (
                    <SectionCard title="Current Subscription">
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: "block", mb: 0.5 }}
                                >
                                    Status
                                </Typography>
                                {getStatusChip(subscription.status)}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: "block", mb: 0.5 }}
                                >
                                    {/* Empty */}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontFamily: "monospace",
                                        fontSize: "0.875rem",
                                    }}
                                >
                                    {/* Empty */}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: "block", mb: 0.5 }}
                                >
                                    Current period start
                                </Typography>
                                <Typography variant="body2">
                                    {formatDate(
                                        subscription.currentPeriodStart,
                                    )}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: "block", mb: 0.5 }}
                                >
                                    Current period end
                                </Typography>
                                <Typography variant="body2">
                                    {formatDate(subscription.currentPeriodEnd)}
                                </Typography>
                            </Grid>
                            {subscription.cancelAtPeriodEnd && (
                                <Grid item xs={12}>
                                    <Chip
                                        label="Subscription will cancel at period end"
                                        color="warning"
                                        size="small"
                                        icon={<WarningIcon />}
                                    />
                                </Grid>
                            )}
                        </Grid>

                        <Divider sx={{ my: 2 }} />

                        <Button
                            variant="outlined"
                            onClick={handleManageSubscription}
                            disabled={loading}
                            startIcon={<CreditCardIcon />}
                            sx={{ width: "100%" }}
                        >
                            Manage Subscription
                        </Button>
                    </SectionCard>
                )}

                <SectionCard title="Account">
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                        }}
                    >
                        <Typography variant="caption" color="text.secondary">
                            Logging out will end your current session. You can
                            log back in anytime.
                        </Typography>
                        <Button
                            variant="outlined"
                            color="error"
                            onClick={logout}
                            startIcon={<LogoutIcon />}
                            sx={{ width: "100%" }}
                        >
                            Logout
                        </Button>
                    </Box>
                </SectionCard>
            </Box>
        </Container>
    );
}
